"use client";

import { type FC, type ReactNode } from "react";

import { shallowEqual } from "react-redux";

import { type UserRolesEnum } from "@/providers/store/api/auth/auth.enum";
import { useAppSelector } from "@/providers/store/hooks";

import { resolveHasAccess } from "./role-based-render.utils";

export interface IRoleBasedRenderProps {
  allowedRoles: UserRolesEnum[];
  children: ReactNode | ((hasAccess: boolean) => ReactNode);
  scope?: "global" | "district";
}

const RoleBasedRender: FC<IRoleBasedRenderProps> = ({
  allowedRoles,
  children,
  scope = "global",
}) => {
  const profile = useAppSelector((state) => state.user.profile, shallowEqual);
  const districtId = useAppSelector(
    (state) => state.district.selectedDistrict?.id ?? null,
    shallowEqual,
  );

  const districtRole = districtId
    ? (profile?.districtRoles?.[districtId] ?? null)
    : null;
  const districtRoleLoading = scope === "district" && !profile;

  const hasAccess = resolveHasAccess({
    allowedRoles,
    scope,
    profileRole: profile?.role,
    districtRole,
    districtRoleLoading,
  });

  if (children instanceof Function) {
    return <>{children(hasAccess)}</>;
  }

  if (hasAccess) return children;

  return null;
};

export default RoleBasedRender;
